import { Fragment, useState, ChangeEvent } from 'react'
import {
  Text,
  Button,
  Icon,
  TextField
} from '@leroy-merlin-br/backyard-react'
import { Checkmark } from '@leroy-merlin-br/backyard-icons'

import mask from 'utils/maskingUtils'
import formatPriceToBRL from 'utils/formatPriceToBRL'

import * as S from './styled'
import { RangeProps, UseRangeProps } from '../../PriceRange'

const CustomPriceRange = ({ range, refine }: UseRangeProps) => {
  const minRange = range.min || 0
  const maxRange = range.max || 0

  const maxValueAccepted = maxRange - 1

  const [currentValues, setCurrentValues] = useState({ min: 0, max: 0 })
  const [hasErrors, setHasErrors] = useState({ min: false, max: false })

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = target
    const formattedValue = parseInt(value.replace(/\D/g, ''), 10)
    const finalValue = isNaN(formattedValue) ? 0 : formattedValue

    if (name) {
      setCurrentValues((previousObj: RangeProps) => {
        const updatedValues = { ...previousObj, [name]: finalValue }

        validateValues(updatedValues, name)

        return updatedValues
      })
    }
  }

  const handleSubmit = () => {
    refine([currentValues.min, currentValues.max])

    setCurrentValues({ min: currentValues.min, max: currentValues.max })
  }

  const validateValues = ({ min, max }: RangeProps, attr: string) => {
    const defaultState = { min: false, max: false }

    if (max && max > maxValueAccepted) {
      setHasErrors({ min: false, max: true })
      return
    }

    if (min && min < minRange) {
      setHasErrors({ min: true, max: false })
      return
    }

    if (min !== 0 && max !== 0) {
      setHasErrors(prevState => {
        const state = Boolean(min >= max)

        return state ? { ...prevState, [attr]: state } : defaultState
      })
    } else {
      setHasErrors(defaultState)
    }
  }

  const validateField = (field: 'min' | 'max') => {
    if (hasErrors[field]) {
      return 'invalid'
    }

    if (currentValues[field]) {
      return 'valid'
    }
  }

  const submitButtonDisabled = Boolean(!currentValues.min && !currentValues.max) || hasErrors.min || hasErrors.max

  const handleValue = (value: 'min' | 'max') => {
    return currentValues[value] !== 0 ? mask.maskedText('money', currentValues[value]) : null
  }

  const handleEnterKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <Fragment>
      <S.MainWrapper isDisabled={submitButtonDisabled}>
        <TextField
          name="min"
          onChange={handleChange}
          onKeyDown={handleEnterKeyPress}
          value={handleValue('min')}
          placeholder="Mínimo"
          state={validateField('min')}
          size="kilo"
          autoComplete="off"
        />

        <Text
          as="span"
          size="kilo"
          color="n600"
          noMargin
        >
          até
        </Text>

        <TextField
          name="max"
          onChange={handleChange}
          onKeyDown={handleEnterKeyPress}
          value={handleValue('max')}
          placeholder="Máximo"
          state={validateField('max')}
          size="kilo"
          autoComplete="off"
        />

        <Button
          size="mega"
          onClick={handleSubmit}
          isDisabled={submitButtonDisabled}
        >
          <Icon as={Checkmark} size="giga" />
        </Button>
      </S.MainWrapper>
      {validateField('min') === 'invalid' && (
        <Text size="kilo" noMargin color="r700">Preço mínimo: {formatPriceToBRL(minRange)}</Text>
      )}
      {validateField('max') === 'invalid' && (
        <Text size="kilo" noMargin color="r700">Preço máximo: {formatPriceToBRL(maxValueAccepted)}</Text>
      )}
    </Fragment>
  )
}

export default CustomPriceRange
